import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  styled,
} from '@mui/material';

import { CurrencyCode } from 'further-types/lp/vehicle';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import React, { useState } from 'react';
import useAllocationChangeHandler from '../CapitalCallAllocations/hooks/useAllocationChangeHandler';
import useAllocateShareCapital from './hooks/useAllocateShareCapital';

const StyledAccordion = styled(Accordion)({
  width: '100%',
});

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  ...theme.typography.h6,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const AccordionBody = styled(AccordionDetails)({
  padding: 0,
  border: 'none',
});

type Props = {
  title: string;
  calledFundsLabel: string;
  currency: CurrencyCode;
  vehicleId: string;
  actionId?: string;
  onChange: (
    capitalBreakdown: Array<{
      commitmentId: string;
      amountLcy: number;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => void;
  capitalBreakdown: Array<{
    commitmentId: string;
    amountLcy: number;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
};

const ShareCapitalCallAllocations: React.FC<Props> = ({
  title,
  currency,
  vehicleId,
  actionId,
  onChange,
  capitalBreakdown,
  calledFundsLabel,
}) => {
  const [excludedCommitmentIds, setExcludedCommitmentIds] = useState<
    Array<string>
  >(
    capitalBreakdown
      .filter((cb) => cb.amountLcy === 0)
      .map((c) => c.commitmentId),
  );

  const { shareCapitalAllocations, isPending } = useAllocateShareCapital({
    vehicleId,
    existingBreakdown: capitalBreakdown,
    excludedCommitmentIds,
    onSuccess: (allocations) => {
      onChange(allocations);
    },
  });

  const handleAllocationChange = useAllocationChangeHandler({
    capitalBreakdown,
    onAllocationsChange: onChange,
    keepCallAmountFixed: false,
  });

  const columns = [
    {
      label: 'Investor',
      key: 'investorLabel',
    },
    {
      label: 'Total capital contribution',
      key: 'totalCommitmentLcy',
    },
    {
      label: 'Uncalled capital contribution',
      key: 'unallocatedCommitmentLcy',
    },
    {
      label: 'Close date',
      key: 'closeDate',
    },
    {
      label: calledFundsLabel,
      key: 'shareCapitalCalled',
      render: (row: { commitmentId: string }) => {
        const capital = capitalBreakdown.find(
          (c) => c.commitmentId === row.commitmentId,
        );

        return (
          <NumberInput
            value={capital?.fundsCalled?.fromCommitmentLcy ?? 0}
            onChange={(value) => {
              handleAllocationChange(
                row.commitmentId,
                'fromCommitmentLcy',
                value ?? 0,
              );
            }}
            decimals={5}
            currency={currency}
            placeholder="Enter amount"
            disabled={true}
          />
        );
      },
    },
    {
      label: 'Exclude from allocation',
      key: 'excludeFromAllocation',
      render: (row: {
        commitmentId: string;
        excludeFromAllocation: boolean;
        unallocatedCommitmentRaw: number;
      }) => (
        <Checkbox
          disabled={
            isPending || !!actionId || row.unallocatedCommitmentRaw === 0
          }
          checked={
            row.excludeFromAllocation || row.unallocatedCommitmentRaw === 0
          }
          onChange={(e) => {
            setExcludedCommitmentIds(
              e.target.checked
                ? [...excludedCommitmentIds, row.commitmentId]
                : excludedCommitmentIds.filter((id) => id !== row.commitmentId),
            );
          }}
        />
      ),
    },
  ];

  const rows = shareCapitalAllocations.map((allocation) => ({
    id: allocation.commitmentId,
    commitmentId: allocation.commitmentId,
    investorLabel: allocation.investorLabel,
    totalCommitmentLcy: numberToCurrencyString(allocation.totalCommitmentLcy, {
      currency,
    }),
    unallocatedCommitmentLcy: numberToCurrencyString(
      allocation.unallocatedCommitmentLcy,
      { currency },
    ),
    closeDate: allocation.closeDate.toLabel(),
    excludeFromAllocation: allocation.excludeFromAllocation,
    unallocatedCommitmentRaw: allocation.unallocatedCommitmentLcy,
  }));

  return (
    <StyledAccordion defaultExpanded>
      <AccordionHeader expandIcon={<ExpandMoreIcon />}>{title}</AccordionHeader>
      <AccordionBody>
        <Table
          showHeaderBorder
          rows={rows}
          columns={columns}
          isLoading={isPending}
        />
      </AccordionBody>
    </StyledAccordion>
  );
};

export default ShareCapitalCallAllocations;
